import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence, useInView, useScroll, useTransform } from 'framer-motion';
import { ZoomIn } from 'lucide-react';
import { useTheme, THEME_USES_VIEW_TRANSITION } from '../../context/ThemeContext';
import ProductTourMobile from './ProductTourMobile';
import { productTourSteps, stableStepHeight } from './productTourSteps';

const EASE_REFINED = [0.22, 1, 0.36, 1];
/** Match --theme-transition-duration in index.css */
const THEME_CROSSFADE_MS = 750;
const THEME_CROSSFADE_EASE = [0.25, 0.46, 0.45, 0.94];
const LOUPE_WIDTH = 300;
const LOUPE_HEIGHT = 160;
const MAGNIFY = 2;
/** Logical width shown in the layout; assets in /public/product are 2× for retina. */
const PRODUCT_DISPLAY_WIDTH = 1024;

const steps = productTourSteps;

function getContainedImageRect(img, elementW, elementH) {
  const { naturalWidth, naturalHeight } = img;
  if (!naturalWidth || !naturalHeight) {
    return { x: 0, y: 0, width: elementW, height: elementH };
  }
  const scale = Math.min(elementW / naturalWidth, elementH / naturalHeight);
  const width = naturalWidth * scale;
  const height = naturalHeight * scale;
  return { x: (elementW - width) / 2, y: 0, width, height };
}

function ThemedScreenshotImages({ step, isDark, alt, magnifierActive, imageRef, isThemeTransitioning }) {
  const srcDark = step.image;
  const srcLight = step.imageLight;
  const srcSet = (src) => `${src} ${step.width * 2}w`;
  const crossfade = {
    duration: isThemeTransitioning && THEME_USES_VIEW_TRANSITION ? 0 : THEME_CROSSFADE_MS / 1000,
    ease: THEME_CROSSFADE_EASE,
  };

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        aspectRatio: `${step.width} / ${stableStepHeight(step)}`,
      }}
    >
      <motion.img
        ref={isDark ? imageRef : null}
        src={srcDark}
        srcSet={srcSet(srcDark)}
        alt={isDark ? alt : ''}
        aria-hidden={!isDark}
        width={step.width}
        height={stableStepHeight(step)}
        sizes={`(min-width: 1280px) ${PRODUCT_DISPLAY_WIDTH}px, 92vw`}
        decoding="async"
        loading="lazy"
        draggable={false}
        initial={false}
        animate={{ opacity: isDark ? 1 : 0 }}
        transition={crossfade}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          objectPosition: 'top center',
          cursor: magnifierActive && isDark ? 'none' : 'default',
          pointerEvents: isDark ? 'auto' : 'none',
        }}
      />
      <motion.img
        ref={isDark ? null : imageRef}
        src={srcLight}
        srcSet={srcSet(srcLight)}
        alt={!isDark ? alt : ''}
        aria-hidden={isDark}
        width={step.width}
        height={stableStepHeight(step)}
        sizes={`(min-width: 1280px) ${PRODUCT_DISPLAY_WIDTH}px, 92vw`}
        decoding="async"
        loading="lazy"
        draggable={false}
        initial={false}
        animate={{ opacity: isDark ? 0 : 1 }}
        transition={crossfade}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          objectPosition: 'top center',
          cursor: magnifierActive && !isDark ? 'none' : 'default',
          pointerEvents: isDark ? 'none' : 'auto',
        }}
      />
      {/* Screen reader: one description regardless of visible variant */}
      <span className="sr-only">{alt}</span>
    </div>
  );
}

function ScreenshotFrame({ step, isDark, alt, label, aspect, nativeWidth, magnifierActive, isThemeTransitioning }) {
  const imgWrapRef = useRef(null);
  const imageRef = useRef(null);
  const [loupe, setLoupe] = useState(null);
  const visibleSrc = isDark ? step.image : step.imageLight;

  useEffect(() => {
    setLoupe(null);
  }, [isDark]);

  const handleMouseMove = (e) => {
    if (!magnifierActive) return;
    const wrap = imgWrapRef.current;
    const img = imageRef.current;
    if (!wrap || !img) return;

    const wrapRect = wrap.getBoundingClientRect();
    const imgRect = img.getBoundingClientRect();
    const content = getContainedImageRect(img, imgRect.width, imgRect.height);

    const offsetX = imgRect.left - wrapRect.left + content.x;
    const offsetY = imgRect.top - wrapRect.top + content.y;
    const renderedW = content.width;
    const renderedH = content.height;

    const ix = Math.min(renderedW, Math.max(0, e.clientX - imgRect.left - content.x));
    const iy = Math.min(renderedH, Math.max(0, e.clientY - imgRect.top - content.y));

    if (
      e.clientX < imgRect.left + content.x ||
      e.clientX > imgRect.left + content.x + renderedW ||
      e.clientY < imgRect.top + content.y ||
      e.clientY > imgRect.top + content.y + renderedH
    ) {
      setLoupe(null);
      return;
    }

    const bgWidth = renderedW * MAGNIFY;
    const bgHeight = renderedH * MAGNIFY;

    // Loupe is LOUPE_WIDTH×LOUPE_HEIGHT on screen but only shows half that in source pixels at 2×.
    const viewW = LOUPE_WIDTH / MAGNIFY;
    const viewH = LOUPE_HEIGHT / MAGNIFY;

    let sourceX = ix - viewW / 2;
    let sourceY = iy - viewH / 2;
    sourceX = Math.max(0, Math.min(renderedW - viewW, sourceX));
    sourceY = Math.max(0, Math.min(renderedH - viewH, sourceY));

    const loupeLeft = Math.max(
      offsetX,
      Math.min(offsetX + renderedW - LOUPE_WIDTH, offsetX + ix - LOUPE_WIDTH / 2),
    );
    const loupeTop = Math.max(
      offsetY,
      Math.min(offsetY + renderedH - LOUPE_HEIGHT, offsetY + iy - LOUPE_HEIGHT / 2),
    );

    const bgX = -sourceX * MAGNIFY;
    const bgY = -sourceY * MAGNIFY;

    setLoupe({ left: loupeLeft, top: loupeTop, bgX, bgY, bgWidth, bgHeight });
  };

  const handleMouseLeave = () => setLoupe(null);

  return (
    <div
      style={{
        width: '100%',
        maxWidth: `min(${PRODUCT_DISPLAY_WIDTH}px, calc((100vh - 250px) * ${aspect}))`,
        marginInline: 'auto',
        background: 'var(--bg-card)',
        border: '2px solid var(--border-strong)',
        borderRadius: 14,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '11px 16px',
          background: 'var(--bg-s1)',
          borderBottom: '1px solid var(--border-strong)',
        }}
      >
        <div style={{ display: 'flex', gap: 6 }}>
          {[0, 1, 2].map((i) => (
            <span key={i} style={{ width: 9, height: 9, borderRadius: '50%', background: 'var(--border-strong)' }} />
          ))}
        </div>
        <span
          style={{
            marginLeft: 6,
            fontFamily: 'Inter, DM Sans, sans-serif',
            fontSize: '0.6875rem',
            letterSpacing: '0.04em',
            color: 'var(--text-muted)',
            fontWeight: 500,
          }}
        >
          {label}
        </span>
      </div>

      <div
        ref={imgWrapRef}
        style={{ position: 'relative', lineHeight: 0 }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <ThemedScreenshotImages
          step={step}
          isDark={isDark}
          alt={alt}
          magnifierActive={magnifierActive}
          imageRef={imageRef}
          isThemeTransitioning={isThemeTransitioning}
        />

        {magnifierActive && loupe && (
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: loupe.left,
              top: loupe.top,
              width: LOUPE_WIDTH,
              height: LOUPE_HEIGHT,
              borderRadius: 10,
              border: '3px solid var(--border-strong)',
              boxShadow: '0 10px 36px var(--shadow-card), inset 0 0 0 1px rgba(255,255,255,0.08)',
              overflow: 'hidden',
              pointerEvents: 'none',
              zIndex: 10,
              backgroundColor: 'var(--bg-base)',
              backgroundImage: `url(${visibleSrc})`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: `${loupe.bgWidth}px ${loupe.bgHeight}px`,
              backgroundPosition: `${loupe.bgX}px ${loupe.bgY}px`,
            }}
          />
        )}
      </div>
    </div>
  );
}

function ProductTourDesktop({ embedded = false }) {
  const { isDark, isThemeTransitioning } = useTheme();
  const sentinelRef = useRef(null);
  const inView = useInView(sentinelRef, { once: true, margin: '-10%' });

  const { scrollYProgress } = useScroll({
    target: sentinelRef,
    offset: ['start start', 'end end'],
  });

  const activeStepMotion = useTransform(scrollYProgress, [0, 0.33, 0.66, 1], [0, 1, 2, 2]);

  const [activeStep, setActiveStep] = useState(0);
  const [magnifierActive, setMagnifierActive] = useState(false);

  useEffect(() => {
    return activeStepMotion.on('change', (v) => {
      const stepped = Math.min(steps.length - 1, Math.floor(v + 0.05));
      setActiveStep(stepped);
    });
  }, [activeStepMotion]);

  useEffect(() => {
    setMagnifierActive(false);
  }, [activeStep]);

  useEffect(() => {
    steps.forEach((step) => {
      [step.image, step.imageLight].forEach((src) => {
        const img = new Image();
        img.src = src;
      });
    });
  }, []);

  const current = steps[activeStep] || steps[0];
  const aspect = current.width / stableStepHeight(current);

  return (
    <div
      ref={sentinelRef}
      style={{ position: 'relative', height: '360vh', background: embedded ? 'transparent' : 'var(--bg-s1)' }}
      id="product"
    >
      <div style={{ position: 'sticky', top: 0, height: '100vh', overflow: 'hidden' }}>
        <section
          style={{
            background: embedded ? 'transparent' : 'var(--bg-s1)',
            borderTop: embedded ? 'none' : '1px solid var(--border-divider)',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            padding: '24px 0',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-10 w-full relative" style={{ zIndex: 1 }}>
            <div className="mb-6 lg:mb-8">
              <motion.h2
                className="landing-heading"
                initial={{ opacity: 0, y: 16 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, ease: EASE_REFINED, delay: 0.1 }}
                style={{
                  fontSize: 'clamp(1.875rem, 3.5vw, 2.75rem)',
                  lineHeight: 1.1,
                  color: 'var(--text-primary)',
                  letterSpacing: '-0.025em',
                  marginBottom: '0.75rem',
                }}
              >
                See Stavyn{' '}
                <span className="headline-accent">
                  in action.
                </span>
              </motion.h2>

              <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
                <div style={{ minHeight: 52, maxWidth: 760 }}>
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={activeStep}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.4, ease: EASE_REFINED }}
                    >
                      <p
                        style={{
                          fontFamily: 'DM Sans, Inter, sans-serif',
                          fontWeight: 400,
                          fontSize: 'clamp(1rem, 1.6vw, 1.125rem)',
                          lineHeight: 1.6,
                          color: 'var(--text-secondary)',
                        }}
                      >
                        {current.title}
                      </p>
                    </motion.div>
                  </AnimatePresence>
                </div>

                <div className="flex lg:items-end shrink-0">
                  <div style={{ display: 'flex', gap: 6 }}>
                    {steps.map((_, i) => (
                      <div
                        key={i}
                        style={{
                          width: activeStep === i ? 24 : 7,
                          height: 7,
                          borderRadius: 4,
                          background: activeStep === i ? 'var(--accent-blue)' : 'var(--border-strong)',
                          transition: 'all 0.35s cubic-bezier(0.22, 1, 0.36, 1)',
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div style={{ position: 'relative', marginTop: '1.25rem' }}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeStep}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5, ease: EASE_REFINED }}
                  className="relative flex w-full justify-center"
                >
                  <div
                    className="min-w-0"
                    style={{
                      width: 'calc(100% - 52px - 0.75rem)',
                      maxWidth: `min(${PRODUCT_DISPLAY_WIDTH}px, calc((100vh - 250px) * ${aspect}))`,
                    }}
                  >
                    <ScreenshotFrame
                      step={current}
                      isDark={isDark}
                      alt={current.alt}
                      label={current.label}
                      aspect={aspect}
                      nativeWidth={current.width}
                      magnifierActive={magnifierActive}
                      isThemeTransitioning={isThemeTransitioning}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setMagnifierActive((v) => !v)}
                    className="absolute top-1/2 -translate-y-1/2 flex shrink-0 items-center justify-center rounded-xl transition-colors hover:opacity-90"
                    style={{
                      right: 0,
                      width: 52,
                      height: 52,
                      background: magnifierActive ? 'var(--btn-primary-bg)' : 'var(--bg-card)',
                      border: '2px solid var(--border-strong)',
                      color: magnifierActive ? 'var(--btn-primary-text)' : 'var(--text-secondary)',
                    }}
                    aria-label={magnifierActive ? 'Turn off magnifier' : 'Turn on magnifier'}
                    aria-pressed={magnifierActive}
                    title={magnifierActive ? 'Magnifier on. Hover over image' : 'Magnify image on hover'}
                  >
                    <ZoomIn className="w-5 h-5" strokeWidth={1.75} />
                  </button>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

export default function ProductTour({ embedded = false }) {
  return (
    <>
      <div className="hidden md:block">
        <ProductTourDesktop embedded={embedded} />
      </div>
      <div className="md:hidden">
        <ProductTourMobile embedded={embedded} />
      </div>
    </>
  );
}
